import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { map, size, noop } from 'lodash';

// local dependencies
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { Icon } from 'modules/icon-bank';
import { getLabel } from 'utils/getLabel';
import { OutsideClickHandler } from 'vendors/OutsideClickHandler';
import { OffCallDispositionNotes } from 'ui/containers/OffCallDispositionNotes';
import { OffCallDispositionActivity } from 'ui/containers/OffCallDispositionActivity';
import { WIDGET_NAMES } from 'constants/entity-names';

// call disposition categories -> widget views
const WIDGET_VIEWS = {
    [ WIDGET_NAMES.OFF_CALL_DISPOSITION_ACTIVITY ]: OffCallDispositionActivity,
    [ WIDGET_NAMES.OFF_CALL_DISPOSITION_NOTES ]: OffCallDispositionNotes,
};

// call disposition categories -> icon names
const ICON_NAMES = {
    offCallDispositionToDos: '',
    offCallDispositionNotes: 'callNotes',
    offCallDispositionActivity: 'callActivity',
};

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { offCallControlWidget as OffCallControlWidget } from 'ui/views/widgets/offCallControlWidget';
 *
 * // render
 * <OffCallControlWidget propName='propValue'/>
 */

// call `active` state view
const OffCallDispositionView = ( props ) => {

    // visibility state
    const [ hidden, setHidden ] = useState( ! props.hasCallEnded );
    useEffect( () => setHidden( ! props.hasCallEnded ), [ props.hasCallEnded ] );

    // extract widgets
    const callDataWidgets = ( props.callDispositionWidgets );

   const callDataWidgetClass= callDataWidgets.length>0?'ui-off-call-control-widget__off-call-disposition':'ui-off-call-control-widget__off-call-disposition__hideData';
    // get width of each section
    const sectionWidth = `${ 100 / size( callDataWidgets ) }%`;

    // categories class name
    const categoriesClassName = classnames( 'ui-off-call-control-widget__off-call-disposition__categories', {
        'ui-off-call-control-widget__off-call-disposition__categories--data-panel-hidden': hidden,
    } );

    // data panel class name
    const dataPanelClassName = classnames( 'ui-off-call-control-widget__off-call-disposition__data-panel', {
        'ui-off-call-control-widget__off-call-disposition__data-panel--hidden': hidden,
    } );

    return (
        <div className={callDataWidgetClass}>

            {/* categories */}
            <div className={ categoriesClassName } data-ignore-outclick='true' onClick={ () => props.hasCallEnded ? noop : setHidden( false ) }>
                {
                    map( callDataWidgets, widget => {
                        return (
                            <div key={ widget.name } className='ui-off-call-control-widget__off-call-disposition__categories__item' style={{ width: sectionWidth }}>

                                {/* icon */}
                                <div className='ui-off-call-control-widget__off-call-disposition__categories__item__icon'>
                                    <Icon name={ ICON_NAMES[ widget.name ] } />
                                </div>

                                {/* title */}
                                <div className='ui-off-call-control-widget__off-call-disposition__categories__item__title'>
                                    { getLabel( widget.title ) }
                                </div>
                            </div>
                        );
                    } )
                }
            </div>
            
            {/* data panel (always when call has ended) */}

            { !hidden ? (
            <OutsideClickHandler disabled={ hidden || props.hasCallEnded } onOutsideClick={ () => setHidden( true ) }>
                <div className={ dataPanelClassName }>

                    {/* call data widgets */}
                    <div className='ui-off-call-control-widget__off-call-disposition__data-panel__call-data-widgets'>
                        {
                            map( callDataWidgets, widget => {

                                // get widget view
                                const WidgetView = WIDGET_VIEWS[ widget.name ];
                                
                                return (
                                    <div key={ widget.name } className='ui-off-call-control-widget__off-call-disposition__data-panel__call-data-widgets__item' style={{ width: sectionWidth }}>
                                        <WidgetView
                                            widget={ widget }
                                            memberId={ props.memberId }
                                            patientId= { props.patientId }
                                        />
                                    </div>
                                );
                            } )
                        }
                    </div>
                </div>
            </OutsideClickHandler>
        ) : null }

        </div>
    );
};


export const offCallControlWidgetSuccess = ( props ) => {

    return (
        <div className='ui-off-call-control-widget'>

            <div className='ui-off-call-control-widget__action'>
                {
                    map( props.navActions, navAction => {

                        // get the action for navAction
                        const iconId = 'back' === navAction.name ? 'backArrow': null;

                        return (
                            <div className='ui-off-call-control-widget__action__nav-action' key={ navAction.name }
                                onClick={ () => props.onNavActionClick( { type: 'headerAction', value: navAction } ) }
                            >
                                <Icon id={ iconId } />
                                <div className='ui-off-call-control-widget__action__nav-action__label'>{ getLabel( navAction.title ) }</div>
                            </div>
                        );

                    } )
                }
            </div>

            <OffCallDispositionView
                { ...props }
                hasCallEnded = { false }
            />
        </div>
    );
};

// set display name
offCallControlWidgetSuccess.displayName = 'offCallControlWidgetSuccess';

// set default props
offCallControlWidgetSuccess.defaultProps = {};

// prop types
offCallControlWidgetSuccess.propTypes = {};

/*******************************/

// preloader view
export const offCallControlWidgetPreloader = () => {
    return (
        <div className='ui-call-control-widget-preloader'>
            <div className='ui-call-control-widget-preloader__left'>
                <div className='ui-call-control-widget-preloader__left__text' />
                <div className='ui-call-control-widget-preloader__left__button' />
            </div>

            <div className='ui-call-control-widget-preloader__right'>
                {
                    map( [ 1, 2 ], item => {
                        return (
                            <div key={ item } className='ui-call-control-widget-preloader__right__panel'>
                                <div className='ui-call-control-widget-preloader__right__panel__icon' />
                                <div className='ui-call-control-widget-preloader__right__panel__text' />
                            </div>
                        );
                    } )
                }
            </div>
        </div>
    );
};

// set display name
offCallControlWidgetPreloader.displayName = 'offCallControlWidgetPreloader';

// set default props
offCallControlWidgetPreloader.defaultProps = {};

// prop types
offCallControlWidgetPreloader.propTypes = {};

/*******************************/

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { callControlWidget as CallControlWidget } from 'ui/views/widgets/callControlWidget';
 *
 * // render
 * <CallControlWidget propName='propValue'/>
 */
export const offCallControlWidget = withNetworkState( offCallControlWidgetPreloader, offCallControlWidgetSuccess );
